import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { GuestUser } from "@scrile/api-provider/dist/api/GuestUsersProvider";
import { LiveChatUser } from "@scrile/api-provider/dist/api/LivechatsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { TipSubjectType } from "@scrile/api-provider/dist/api/TipProvider";
import { debounce } from "lodash-es";
import { createPortal } from "react-dom";
import config from "config";
import { useTranslation } from "locales";
import useDrafts from "hooks/useDrafts";
import useMobileKeyboardOpen from "hooks/useMobileKeyboard";
import emitter, { EVENTS } from "lib/emitter";
import { hapticLight } from "lib/haptics";
import { stripMetaPrefix } from "lib/messageMetadata";
import { takePendingShared } from "lib/pendingSharedFiles";
import { isPerformer } from "lib/usersHelper";
import TipModal from "modals/TipModal";
import BaseIcon from "components/BaseIcon";
import BaseTextarea from "components/BaseTextarea";
import EmojiPicker from "components/EmojiPicker";
import FormatToolbar from "components/FormatToolbar";
import GifPicker from "components/GifPicker";
import MentionAutocomplete from "components/MentionAutocomplete";
import MessageAttachView from "components/MessageAttachView";
import VideoRecorder from "components/VideoRecorder";
import VoiceRecorder from "components/VoiceRecorder";
import MessageAttachButton from "./components/MessageAttachButton";
import useController, { neutralizeSystemPrefix } from "./controller";
import useViewController from "./viewController";
import "./style.scss";

// Format a Date as the LOCAL wall-clock string a <input type="datetime-local">
// expects ("YYYY-MM-DDTHH:mm"). `toISOString()` alone emits UTC digits, but the
// input both displays and reads back its value as LOCAL time — so a UTC-formatted
// value is off by the timezone offset, which (with the future-time guard in
// handleScheduleSend) made the pre-filled default un-submittable for +UTC users.
// Subtracting the offset first makes toISOString() print the local wall time.
function toLocalDatetimeInputValue(d: Date): string {
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

// Reply-quote metadata carried in the __META__ envelope. One builder for both
// send paths (immediate sendWithMetadata + scheduled) so the wire shape can't
// drift between them.
const buildReplyMeta = (msg: Message) => ({
  replyToMessageId: msg.id,
  replyToText: stripMetaPrefix(msg.text).substring(0, 100),
  replyToSenderId: msg.senderId,
  replyToSenderName: (msg as any).senderUser?.screenName || "",
});

const formatSendError = (e: unknown) =>
  Array.isArray(e)
    ? e.map((err: any) => `[${err.code}] ${err.details?.propertyPath || "?"}: ${err.message}`).join("; ")
    : e;

interface Props {
  disableAttachments?: boolean;
  onFocus?: (e: React.FocusEvent) => void;
  className?: string;
  maxRows?: number;
  transparentOnBlur?: boolean;
  userIds?: string[];
  threadIds?: string[];
  allowGuestMessage?: boolean;
  guestUser?: GuestUser | null;
  setGuestUser?: (guest: GuestUser) => void;
  me?: LiveChatUser;
  replyToMessage?: Message | null;
  onClearReply?: () => void;
  onTyping?: () => void;
  hideTip?: boolean;
}

function MessageInput({
  userIds,
  threadIds,
  disableAttachments = false,
  onFocus,
  className,
  transparentOnBlur,
  allowGuestMessage,
  guestUser,
  setGuestUser,
  me,
  maxRows = 5,
  replyToMessage,
  onClearReply,
  onTyping,
  hideTip,
}: Props) {
  const { t } = useTranslation();
  const {
    user,
    value,
    setValue,
    onSendMessage,
    sendWithMetadata,
    sendRawText,
    sendFileWithMeta,
    transcribeUploadedFile,
    scheduleMessage,
    setTextarea,
    attachedFiles,
    sendQueued,
    addAttachment,
    removeAttachment,
  } = useController(userIds, threadIds, allowGuestMessage, guestUser, setGuestUser, me);
  const { showTipModal, setShowTipModal } = useViewController();
  const { keyboardOpen } = useMobileKeyboardOpen();
  const { saveDraft, getDraft, clearDraft } = useDrafts();
  const currentThreadId = threadIds?.[0];
  const valueRef = useRef(value);
  valueRef.current = value;
  // Which thread's draft is currently loaded into `value`. The typing-save effect
  // below only writes when this matches currentThreadId, so the cross-thread
  // render (where `value` still holds the previous thread's text) can't clobber
  // the new thread's draft.
  const loadedThreadRef = useRef<string | undefined>(undefined);

  const debouncedSave = useMemo(
    () => debounce((tid: string, text: string) => saveDraft(tid, { text }), 400),
    [saveDraft]
  );

  // useLayoutEffect so the textarea never paints a frame of the previous thread's text.
  React.useLayoutEffect(() => {
    const prev = loadedThreadRef.current;
    if (prev && prev !== currentThreadId) {
      debouncedSave.cancel();
      saveDraft(prev, { text: valueRef.current });
    }
    loadedThreadRef.current = currentThreadId;
    if (currentThreadId) {
      const draft = getDraft(currentThreadId);
      setValue(draft?.text || "");
    } else {
      setValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThreadId]);

  // Pick up content shared into the app from the OS share sheet (see
  // useShareTarget + ShareTargetModal). The picker stashes it keyed by the
  // target thread id and navigates here; we consume-once into the existing
  // attachment pipeline (files) and seed the composer (text / link).
  const applyShared = React.useCallback(
    (shared: ReturnType<typeof takePendingShared>) => {
      if (!shared) return;
      if (shared.files.length) addAttachment(shared.files);
      const txt = shared.text;
      if (txt) setValue((prev) => (prev ? `${prev}\n${txt}` : txt));
    },
    [addAttachment, setValue]
  );

  // Consume a pending OS-share for this composer. Tries the real thread id
  // first, then a `user:<id>` key — a brand-new self-chat (Saved Messages with
  // no thread yet) is keyed by user id because its thread doesn't exist until
  // the first message. Consume-once (takePendingShared deletes on read).
  const consumeSharedContent = React.useCallback(
    (tid?: string) => {
      if (disableAttachments) return;
      if (tid) {
        const byThread = takePendingShared(tid);
        if (byThread) return applyShared(byThread);
      }
      const uid = userIds?.[0];
      if (uid) applyShared(takePendingShared(`user:${uid}`));
    },
    [disableAttachments, applyShared, userIds]
  );

  // Cross-chat / cold open: fires when the thread id transitions to the target.
  React.useEffect(() => {
    consumeSharedContent(currentThreadId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThreadId, userIds]);

  // Same-chat: sharing into the chat already on screen is a no-op navigation
  // (currentThreadId unchanged) so the effect above won't refire — the picker
  // signals us explicitly via SHARE_HANDOFF.
  React.useEffect(() => {
    const onHandoff = (p: { threadId?: string }) => {
      // Match by thread id OR the user:<id> key of a not-yet-created self-chat.
      if (!p?.threadId) return;
      if (p.threadId === currentThreadId || p.threadId === `user:${userIds?.[0]}`) {
        consumeSharedContent(currentThreadId);
      }
    };
    emitter.on(EVENTS.SHARE_HANDOFF, onHandoff);
    return () => emitter.off(EVENTS.SHARE_HANDOFF, onHandoff);
  }, [currentThreadId, userIds, consumeSharedContent]);

  React.useEffect(() => {
    if (!currentThreadId || loadedThreadRef.current !== currentThreadId) return;
    debouncedSave(currentThreadId, value);
  }, [value, currentThreadId, debouncedSave]);

  // Flush on unmount so closing the tab mid-type never loses the draft.
  // Empty deps intentional: saveDraft/debouncedSave are stable (useCallback [] /
  // useMemo on stable saveDraft), and we only want cleanup to fire at real unmount —
  // not on every re-render, which would repeatedly write to localStorage.
  React.useEffect(() => {
    return () => {
      const tid = loadedThreadRef.current;
      debouncedSave.cancel();
      if (tid) saveDraft(tid, { text: valueRef.current });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const textareaLocalRef = useRef<HTMLTextAreaElement | null>(null);
  const handleSetTextarea = useCallback(
    (el: HTMLTextAreaElement | null) => {
      textareaLocalRef.current = el;
      setTextarea(el);
    },
    [setTextarea]
  );
  const [isDragging, setIsDragging] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showGif, setShowGif] = useState(false);
  const gifWrapperRef = useRef<HTMLDivElement>(null);
  const gifButtonRef = useRef<HTMLButtonElement>(null);
  const [gifAnchor, setGifAnchor] = useState<{ top: number; right: number } | null>(null);
  const emojiWrapperRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const [emojiAnchor, setEmojiAnchor] = useState<{ top: number; right: number } | null>(null);

  // Measure GIF button on open so the portal can anchor above it.
  //
  // We render the picker into document.body via createPortal because the
  // parent <.message-input> uses `backdrop-filter` (glassmorphism), which
  // per CSS spec turns it into the containing block for `position: fixed`
  // descendants. An anchored fixed picker inside .message-input would be
  // positioned relative to that narrow 50px-tall bar, effectively
  // invisible on mobile. Portal sidesteps the issue cleanly.
  useLayoutEffect(() => {
    if (!showGif) {
      setGifAnchor(null);
      return;
    }
    const update = () => {
      const rect = gifButtonRef.current?.getBoundingClientRect();
      if (rect) setGifAnchor({ top: rect.top, right: rect.right });
    };
    update();
    const scrollOpts: AddEventListenerOptions = { passive: true, capture: true };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, scrollOpts);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, scrollOpts);
    };
  }, [showGif]);

  // Close GIF picker on outside tap/click. We use `pointerdown` rather than
  // `mousedown` so the same handler works for mouse, touch and Apple Pencil
  // — on Capacitor WKWebView mobile tap may not fire `mousedown` reliably.
  // We also exclude pointers landing on the GIF button itself — otherwise
  // this handler would close the picker and the button's onClick would
  // immediately re-toggle it back open.
  useEffect(() => {
    if (!showGif) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (gifWrapperRef.current?.contains(target)) return;
      if (gifButtonRef.current?.contains(target)) return;
      setShowGif(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [showGif]);

  // Emoji picker: same portal + viewport-anchored positioning as the GIF
  // picker. Rendered inline (right:0 inside the narrow in-call chat input) it
  // overflowed 320px off the LEFT edge; a body-portal with a measured anchor
  // and a viewport clamp keeps it on-screen everywhere.
  useLayoutEffect(() => {
    if (!showEmoji) {
      setEmojiAnchor(null);
      return;
    }
    const update = () => {
      const rect = emojiButtonRef.current?.getBoundingClientRect();
      if (rect) setEmojiAnchor({ top: rect.top, right: rect.right });
    };
    update();
    const scrollOpts: AddEventListenerOptions = { passive: true, capture: true };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, scrollOpts);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, scrollOpts);
    };
  }, [showEmoji]);

  useEffect(() => {
    if (!showEmoji) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as Node;
      if (emojiWrapperRef.current?.contains(target)) return;
      if (emojiButtonRef.current?.contains(target)) return;
      setShowEmoji(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [showEmoji]);

  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceSending, setVoiceSending] = useState(false);
  const [videoRecording, setVideoRecording] = useState(false);
  const [videoSending, setVideoSending] = useState(false);
  const [showFormatToolbar, setShowFormatToolbar] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleError, setScheduleError] = useState("");
  const sendLongPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sendLongPressActivated = useRef(false);

  // Clean up long-press timer on unmount
  React.useEffect(() => {
    return () => {
      if (sendLongPressTimer.current) clearTimeout(sendLongPressTimer.current);
    };
  }, []);

  const mentionUserIds = useMemo(() => userIds || [], [userIds]);

  const handleEmojiSelect = useCallback(
    (emoji: string) => {
      setValue(value + emoji);
    },
    [value, setValue]
  );

  const handleInput = useCallback(
    (newValue: string) => {
      setValue(newValue);
      onTyping?.();
      const atIndex = newValue.lastIndexOf("@");
      if (atIndex >= 0 && (atIndex === 0 || newValue[atIndex - 1] === " ")) {
        const query = newValue.substring(atIndex + 1);
        if (!query.includes(" ") && !query.includes("]")) {
          setMentionQuery(query);
          return;
        }
      }
      setMentionQuery(null);
    },
    [setValue, onTyping]
  );

  const handleMentionSelect = useCallback(
    (u: any) => {
      const atIndex = value.lastIndexOf("@");
      if (atIndex >= 0) {
        const before = value.substring(0, atIndex);
        const mentionText = `@${u.screenName} `;
        setValue(before + mentionText);
      }
      setMentionQuery(null);
    },
    [value, setValue]
  );

  // Send with reply metadata
  const handleSendMessage = useCallback(() => {
    hapticLight();
    if (currentThreadId) clearDraft(currentThreadId);
    const sendPromise = replyToMessage
      ? sendWithMetadata(buildReplyMeta(replyToMessage))
      : Promise.resolve(onSendMessage());
    sendPromise.catch((e: unknown) => {
      // eslint-disable-next-line no-console
      console.error("Failed to send message:", formatSendError(e));
    });
    if (replyToMessage) onClearReply?.();
  }, [replyToMessage, onSendMessage, sendWithMetadata, onClearReply, currentThreadId, clearDraft]);

  // Long-press on send button → schedule modal. Guests (livechat composer with
  // allowGuestMessage and no auth user — the same condition the controller uses
  // to route sends to onSendMessageGuest) never get the modal: scheduleMessage
  // requires auth and would fail server-side on every retry.
  const handleSendPointerDown = useCallback(() => {
    if (allowGuestMessage && !user) return;
    sendLongPressActivated.current = false;
    sendLongPressTimer.current = setTimeout(() => {
      sendLongPressActivated.current = true;
      hapticLight();
      const now = new Date();
      now.setMinutes(now.getMinutes() + 10);
      setScheduleDate(toLocalDatetimeInputValue(now));
      setShowScheduleModal(true);
    }, 600);
  }, [allowGuestMessage, user]);

  const handleSendPointerUp = useCallback(() => {
    if (sendLongPressTimer.current) {
      clearTimeout(sendLongPressTimer.current);
      sendLongPressTimer.current = null;
    }
  }, []);

  const handleSendClick = useCallback(() => {
    if (sendLongPressActivated.current) {
      sendLongPressActivated.current = false;
      return;
    }
    handleSendMessage();
  }, [handleSendMessage]);

  const scheduleSendingRef = React.useRef(false);
  const handleScheduleSend = useCallback(async () => {
    if (!scheduleDate || !currentThreadId) return;
    const text = value.trim();
    if (!text) return;
    // In-flight guard: a double-tap on "Send later" (slow mobile, before the
    // first await resolves) would otherwise POST two identical scheduled rows
    // — the mutation carries no idempotency key, so both deliver.
    if (scheduleSendingRef.current) return;
    scheduleSendingRef.current = true;
    // Scheduled sends are text-only end-to-end (the mutation carries no files).
    // Refuse instead of silently dropping staged attachments — they would
    // otherwise ride along with the NEXT normal send.
    if (attachedFiles.length > 0) {
      setScheduleError(t("Attachments can't be scheduled. Remove them or send now."));
      return;
    }
    // Guard against a past/now datetime: the backend only defers strictly-future
    // sends (parseFutureInstant), so a past value would deliver immediately while
    // the UI implied "scheduled". Reject it here with a clear message instead.
    const whenMs = new Date(scheduleDate).getTime();
    if (isNaN(whenMs) || whenMs <= Date.now()) {
      setScheduleError(t("Pick a time in the future."));
      return;
    }
    // Preserve an active reply exactly like a normal send (same __META__
    // envelope) — otherwise the message dispatches with no reply indicator
    // while the reply banner stays armed and silently quotes the wrong
    // message on the user's next send.
    // Neutralize a user-typed leading system marker BEFORE wrapping in the
    // reply envelope (same guard as buildMessageData; the scheduled path builds
    // its own payload and bypasses buildMessageData).
    const safeText = neutralizeSystemPrefix(text);
    const outgoing = replyToMessage
      ? `__META__:${JSON.stringify(buildReplyMeta(replyToMessage))}\n${safeText}`
      : safeText;
    try {
      await scheduleMessage(currentThreadId, outgoing, new Date(whenMs).toISOString());
      setValue("");
      clearDraft(currentThreadId);
      onClearReply?.();
      setShowScheduleModal(false);
      setScheduleDate("");
      setScheduleError("");
      // Refresh the ScheduledMessages panel — the only surface that reflects the
      // pending send (scheduled rows are filtered out of normal history).
      emitter.emit(EVENTS.SCHEDULED_MESSAGES_CHANGED, { threadId: currentThreadId });
    } catch (e) {
      console.error("Failed to schedule message:", e);
      setScheduleError(t("Couldn't schedule the message. Please try again."));
    } finally {
      scheduleSendingRef.current = false;
    }
  }, [
    scheduleDate,
    currentThreadId,
    value,
    attachedFiles,
    replyToMessage,
    onClearReply,
    scheduleMessage,
    setValue,
    clearDraft,
    t,
  ]);

  // Voice message
  const voiceMetaRef = React.useRef<{ voiceMessage: true; voiceDuration: number } | null>(null);
  const voiceSendingRef = React.useRef(false);
  const [voiceTextOnly, setVoiceTextOnly] = useState(false);
  const [voiceUploadReady, setVoiceUploadReady] = useState<string | null>(null);

  // Stable so VoiceRecorder's start-once effect isn't disturbed (a fresh inline
  // arrow every render used to restart the recording).
  const handleVoiceCancel = useCallback(() => setVoiceRecording(false), []);

  const handleVoiceSend = useCallback(
    async (blob: Blob, duration: number, textOnly: boolean) => {
      setVoiceRecording(false);
      setVoiceSending(true);
      setVoiceTextOnly(textOnly);
      const file = new File([blob], "voice-message.webm", { type: blob.type });
      voiceMetaRef.current = { voiceMessage: true, voiceDuration: Math.round(duration * 10) / 10 };
      setVoiceUploadReady(null);
      try {
        await addAttachment([file]);
      } catch (e) {
        console.error("Voice message upload failed:", e);
        voiceMetaRef.current = null;
        setVoiceTextOnly(false);
        setVoiceSending(false);
      }
    },
    [addAttachment]
  );

  // Detect when voice file upload is fully confirmed (uploadProgress === 100 via onSuccess)
  React.useEffect(() => {
    if (!voiceMetaRef.current || voiceSendingRef.current) return;
    const voiceFile = attachedFiles.find(
      (f) => "uploadProgress" in f && f.uploadProgress === 100 && f.fileName === "voice-message.webm"
    );
    if (voiceFile && voiceUploadReady !== voiceFile.id) {
      // Wait for S3/MinIO to fully commit the uploaded object before sending message
      const timer = setTimeout(() => setVoiceUploadReady(voiceFile.id), 1500);
      return () => clearTimeout(timer);
    }
  }, [attachedFiles, voiceUploadReady]);

  // Send voice message (or voice-to-text) once upload is confirmed
  React.useEffect(() => {
    if (!voiceUploadReady || voiceSendingRef.current || !voiceMetaRef.current) return;
    const meta = voiceMetaRef.current;
    const isTextOnly = voiceTextOnly;
    voiceMetaRef.current = null;
    voiceSendingRef.current = true;

    const doSend = async () => {
      if (isTextOnly) {
        // Voice-to-text: transcribe the uploaded audio, then send as plain text.
        // Keep the transcription call in its OWN try so that only a transcription
        // failure triggers the voice-message fallback — a failure while *sending*
        // the transcribed text must not, or we'd send an unintended voice message
        // (and possibly duplicate a text that already landed server-side).
        let transcript: string | null = null;
        let transcriptionFailed = false;
        try {
          transcript = await transcribeUploadedFile(voiceUploadReady);
        } catch (e) {
          transcriptionFailed = true;
          console.error("Voice-to-text failed; falling back to voice message:", e);
        }

        if (transcriptionFailed) {
          // Don't lose the user's recording: send it as a normal voice message.
          // The audio uploaded fine, so the attachment is still valid here.
          try {
            await sendFileWithMeta(voiceUploadReady, meta);
            alert(t("Couldn't transcribe — sent as a voice message instead."));
          } catch (sendErr) {
            console.error("Voice message fallback also failed:", sendErr);
            alert(t("Couldn't send your voice message. Please try again."));
          } finally {
            removeAttachment(voiceUploadReady);
          }
        } else if (transcript && transcript.trim()) {
          removeAttachment(voiceUploadReady);
          try {
            await sendRawText(transcript.trim());
          } catch (sendErr) {
            // Transcription worked; only the text send failed. Report it — do NOT
            // fall back to a voice message (that would duplicate / mis-send).
            console.error("Sending transcribed text failed:", sendErr);
            alert(t("Couldn't send your voice message. Please try again."));
          }
        } else {
          removeAttachment(voiceUploadReady);
          console.warn("Voice-to-text: transcription returned empty text (silence?)");
          alert(t("No speech detected — nothing to send."));
        }
      } else {
        // Normal voice message flow
        try {
          await sendFileWithMeta(voiceUploadReady, meta);
          removeAttachment(voiceUploadReady);
        } catch (e) {
          console.error("Voice message send failed:", e);
          removeAttachment(voiceUploadReady);
        }
      }
      setVoiceUploadReady(null);
      setVoiceTextOnly(false);
      voiceSendingRef.current = false;
      setVoiceSending(false);
    };

    doSend();
  }, [voiceUploadReady, voiceTextOnly, sendFileWithMeta, removeAttachment, transcribeUploadedFile, sendRawText]);

  // Round video message
  const videoMetaRef = React.useRef<{ videoMessage: true; videoDuration: number } | null>(null);
  const videoSendingRef = React.useRef(false);
  const [videoUploadReady, setVideoUploadReady] = useState<string | null>(null);

  const handleVideoSend = useCallback(
    async (blob: Blob, duration: number) => {
      setVideoRecording(false);
      setVideoSending(true);
      const file = new File([blob], "video-message.webm", { type: "video/webm" });
      videoMetaRef.current = { videoMessage: true, videoDuration: Math.round(duration) };
      setVideoUploadReady(null);
      try {
        await addAttachment([file]);
      } catch (e) {
        console.error("Video message upload failed:", e);
        videoMetaRef.current = null;
        setVideoSending(false);
      }
    },
    [addAttachment]
  );

  React.useEffect(() => {
    if (!videoMetaRef.current || videoSendingRef.current) return;
    const videoFile = attachedFiles.find(
      (f) => "uploadProgress" in f && f.uploadProgress === 100 && f.fileName === "video-message.webm"
    );
    if (videoFile && videoUploadReady !== videoFile.id) {
      const timer = setTimeout(() => setVideoUploadReady(videoFile.id), 1500);
      return () => clearTimeout(timer);
    }
  }, [attachedFiles, videoUploadReady]);

  React.useEffect(() => {
    if (!videoUploadReady || videoSendingRef.current || !videoMetaRef.current) return;
    const meta = videoMetaRef.current;
    videoMetaRef.current = null;
    videoSendingRef.current = true;
    sendFileWithMeta(videoUploadReady, meta)
      .then(() => {
        removeAttachment(videoUploadReady);
        setVideoUploadReady(null);
      })
      .catch((e) => {
        console.error("Video message send failed:", e);
        setVideoUploadReady(null);
      })
      .finally(() => {
        videoSendingRef.current = false;
        setVideoSending(false);
      });
  }, [videoUploadReady, sendFileWithMeta, removeAttachment]);

  // Switching chats abandons any in-flight voice/video send: the recorder
  // attachment is dropped with the previous thread, so the send-detect
  // effects that normally clear these flags never fire and the composer would
  // stay locked on the "Sending voice/video message…" spinner forever. Reset
  // the stuck state on thread change. (Placed after both recorder refs are
  // declared so it can read them.)
  React.useEffect(() => {
    return () => {
      if (voiceSendingRef.current || videoSendingRef.current) {
        voiceSendingRef.current = false;
        videoSendingRef.current = false;
        voiceMetaRef.current = null;
        videoMetaRef.current = null;
        setVoiceSending(false);
        setVideoSending(false);
        setVoiceUploadReady(null);
        setVideoUploadReady(null);
        setVoiceTextOnly(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentThreadId]);

  const cls = ["message-input"];
  if (className) cls.push(className);
  if (transparentOnBlur && !keyboardOpen) cls.push("__transparent");

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (disableAttachments) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      const files: File[] = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (file) files.push(file);
        }
      }
      if (files.length > 0) {
        e.preventDefault();
        addAttachment(files);
      }
    },
    [addAttachment, disableAttachments]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disableAttachments) return;
      const files: File[] = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        addAttachment(files);
      }
    },
    [addAttachment, disableAttachments]
  );

  // Count only real attachments (exclude error cards) so the "N / max" badge
  // never reads "11 / 10" when an over-limit error card is also present.
  const attachmentCount = attachedFiles.filter((file) => !("error" in file)).length;
  const showMicButton = !value.trim() && attachedFiles.length === 0 && !disableAttachments;

  // Voice recording replaces the input area
  if (voiceRecording) {
    return <VoiceRecorder onSend={handleVoiceSend} onCancel={handleVoiceCancel} />;
  }

  // Voice message uploading/sending — show spinner instead of normal input
  if (voiceSending) {
    return (
      <div className="message-input __voice-sending">
        <div className="message-input__voice-sending-indicator">
          <span className="message-input__voice-sending-spinner" />
          <span className="message-input__voice-sending-text">
            {voiceTextOnly ? t("Transcribing voice...") : t("Sending voice message...")}
          </span>
        </div>
      </div>
    );
  }

  // Video message uploading/sending — show spinner
  if (videoSending) {
    return (
      <div className="message-input __voice-sending">
        <div className="message-input__voice-sending-indicator">
          <span className="message-input__voice-sending-spinner" />
          <span className="message-input__voice-sending-text">{t("Sending video message...")}</span>
        </div>
      </div>
    );
  }

  // Round video recording replaces the input area
  if (videoRecording) {
    return <VideoRecorder onRecorded={handleVideoSend} onCancel={() => setVideoRecording(false)} />;
  }

  return (
    <div
      className={cls.join(" ")}
      onClick={(e) => e.stopPropagation()}
      onPaste={handlePaste}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && <div className="message-input__drop-overlay">{t("Drop files here")}</div>}

      {/* Reply preview bar */}
      {replyToMessage && (
        <div className="message-input__reply-bar">
          <div className="message-input__reply-accent" />
          <div className="message-input__reply-content">
            <div className="message-input__reply-name">
              {(replyToMessage as any).senderUser?.screenName || t("User")}
            </div>
            <div className="message-input__reply-text">
              {stripMetaPrefix(replyToMessage.text).substring(0, 80)}
              {stripMetaPrefix(replyToMessage.text).length > 80 ? "..." : ""}
            </div>
          </div>
          <button className="message-input__reply-close" onClick={onClearReply} type="button">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
                fill="currentColor"
              />
            </svg>
          </button>
        </div>
      )}

      <div className="message-input__row">
        {/* The picker stays available while files upload — adding files from
            several directories in a row is the whole point. Individual files
            are removed via the per-file ✕ in MessageAttachView. */}
        {!disableAttachments && (
          <MessageAttachButton
            className="message-input__attach-btn"
            onInput={addAttachment}
            onVideoRecord={() => setVideoRecording(true)}
          />
        )}
        <div className="message-input__input-wrapper">
          {mentionQuery !== null && mentionUserIds.length > 0 && (
            <MentionAutocomplete
              userIds={mentionUserIds}
              query={mentionQuery}
              onSelect={handleMentionSelect}
              onClose={() => setMentionQuery(null)}
            />
          )}
          {attachmentCount > 0 && (
            <div className="message-input__attach-counter small">
              {attachmentCount} / {config.maxAttachmentCount}
            </div>
          )}
          {attachedFiles.map((attachedFile) => (
            <div className="mb-1" key={attachedFile.id}>
              <MessageAttachView attachedFileData={attachedFile} removeAttachment={removeAttachment} />
            </div>
          ))}
          <FormatToolbar
            textareaRef={textareaLocalRef.current}
            value={value}
            onChange={setValue}
            className={showFormatToolbar ? "" : "message-input__format-toolbar-hidden"}
          />
          <div className="message-input__input-inner">
            <BaseTextarea
              setTextarea={handleSetTextarea}
              className="message-input__input"
              value={value}
              type="textarea"
              onInput={handleInput}
              name="message-input"
              placeholder={t("Message")}
              maxRows={maxRows}
              rowHeight={23}
              autoGrow={true}
              rows={1}
              maxLength={3000}
              submit="enter"
              onSubmit={handleSendMessage}
              onFocus={onFocus}
            />
            <div className="message-input__input-icons">
              <button
                className="message-input__icon-btn"
                onClick={() => setShowFormatToolbar(!showFormatToolbar)}
                type="button"
                title={t("Formatting")}
              >
                <span className="message-input__icon-btn-label">Aa</span>
              </button>
              <button
                ref={emojiButtonRef}
                className="message-input__icon-btn"
                onClick={() => {
                  setShowEmoji(!showEmoji);
                  setShowGif(false);
                }}
                type="button"
                title={t("Emoji")}
              >
                <svg
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="9.5" />
                  <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none" />
                  <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none" />
                  <path d="M8.5 14.5c.8 1.5 2 2 3.5 2s2.7-.5 3.5-2" />
                </svg>
              </button>
              {(process.env.REACT_APP_GIF_API_KEY || process.env.REACT_APP_TENOR_API_KEY) && (
                <button
                  ref={gifButtonRef}
                  className="message-input__icon-btn"
                  onClick={() => {
                    setShowGif(!showGif);
                    setShowEmoji(false);
                  }}
                  type="button"
                  title={t("GIF")}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="2" y="4" width="20" height="16" rx="3" />
                    <text
                      x="12"
                      y="15"
                      textAnchor="middle"
                      fontSize="8"
                      fontWeight="700"
                      fill="currentColor"
                      stroke="none"
                    >
                      GIF
                    </text>
                  </svg>
                </button>
              )}
            </div>
          </div>
          {showEmoji &&
            createPortal(
              <div
                ref={emojiWrapperRef}
                className="message-input__gif-portal"
                style={
                  emojiAnchor
                    ? {
                        bottom: `calc(100vh - ${emojiAnchor.top}px + 8px)`,
                        right: `calc(100vw - ${emojiAnchor.right}px)`,
                      }
                    : undefined
                }
              >
                <EmojiPicker onSelect={handleEmojiSelect} onClose={() => setShowEmoji(false)} />
              </div>,
              document.body
            )}
          {showGif &&
            createPortal(
              <div
                ref={gifWrapperRef}
                className="message-input__gif-portal"
                style={
                  gifAnchor
                    ? {
                        // Anchor above the GIF button: place the picker's bottom 8px
                        // above the button's top, and its right edge flush with the
                        // button's right edge. On mobile these are overridden by
                        // viewport-anchored rules in style.scss (!important).
                        bottom: `calc(100vh - ${gifAnchor.top}px + 8px)`,
                        right: `calc(100vw - ${gifAnchor.right}px)`,
                      }
                    : undefined
                }
              >
                <GifPicker
                  onSelect={(url) => {
                    setShowGif(false);
                    sendRawText(url);
                  }}
                />
              </div>,
              document.body
            )}
        </div>
        {/* HIDDEN: credit system — commented for App Store moderation
        {!hideTip && threadIds?.length === 1 && userIds?.length === 1 && !isPerformer(user) && (
          <button className="message-input__tip-btn" onClick={() => setShowTipModal(true)} type="button">
            <BaseIcon>pay-service</BaseIcon>
          </button>
        )}
        */}
        {showMicButton ? (
          <button
            className="message-input__mic-btn"
            onClick={() => setVoiceRecording(true)}
            type="button"
            title={t("Voice message")}
          >
            <svg
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="9" y="2" width="6" height="12" rx="3" />
              <path d="M5 11a7 7 0 0 0 14 0" />
              <path d="M12 18v3M9 21h6" />
            </svg>
          </button>
        ) : (
          <button
            className={`message-input__send-btn${sendQueued ? " __queued" : ""}`}
            onClick={handleSendClick}
            onMouseDown={handleSendPointerDown}
            onMouseUp={handleSendPointerUp}
            onMouseLeave={handleSendPointerUp}
            onTouchStart={handleSendPointerDown}
            onTouchEnd={handleSendPointerUp}
            onTouchCancel={handleSendPointerUp}
            type="button"
            title={sendQueued ? t("Will send when uploads finish") : undefined}
          >
            {sendQueued ? (
              <span className="message-input__send-spinner" />
            ) : (
              <svg
                viewBox="0 0 24 24"
                width="20"
                height="20"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            )}
          </button>
        )}
        {showScheduleModal && (
          <div
            className="message-input__schedule-overlay"
            onClick={() => {
              setShowScheduleModal(false);
              setScheduleError("");
            }}
          >
            <div className="message-input__schedule-modal" onClick={(e) => e.stopPropagation()}>
              <div className="message-input__schedule-title">{t("Schedule message")}</div>
              <input
                className="message-input__schedule-input"
                type="datetime-local"
                value={scheduleDate}
                min={toLocalDatetimeInputValue(new Date())}
                onChange={(e) => {
                  setScheduleDate(e.target.value);
                  setScheduleError("");
                }}
              />
              {scheduleError && <div className="message-input__schedule-error">{scheduleError}</div>}
              <div className="message-input__schedule-actions">
                <button
                  type="button"
                  className="message-input__schedule-cancel"
                  onClick={() => {
                    setShowScheduleModal(false);
                    setScheduleError("");
                  }}
                >
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="message-input__schedule-confirm"
                  onClick={handleScheduleSend}
                  disabled={!scheduleDate || !value.trim()}
                >
                  {t("Send later")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* HIDDEN: credit system — commented for App Store moderation
      {userIds?.length === 1 && !isPerformer(user) && (
        <TipModal
          subject={{ subjectId: userIds[0], subjectType: TipSubjectType.USER }}
          show={showTipModal}
          onClose={() => setShowTipModal(false)}
        />
      )}
      */}
    </div>
  );
}

export default MessageInput;
